import React from "react";
import {
  Shield,
  Lock,
  Eye,
  Users,
  Server,
  Key,
  AlertTriangle,
} from "lucide-react";
import { motion } from "framer-motion";
import PublicHeader from "../../components/PublicHeader/PublicHeader";
import Footer from "../../components/Footer/Footer";
import ReadyToTransform from "../../components/ReadyToTransform/ReadyToTransform";
import Testimonials from "../../components/Testimonials/Testimonials";
import FAQ from "../../components/FAQ/FAQ";

const TEAL = "#00BFA6";

const cardClass =
  "bg-white border border-[#EDF4FF] rounded-2xl p-8 shadow-[0px_4px_20px_-2px_rgba(0,0,0,0.05)]";

const SectionCard = ({ icon: Icon, title, children, iconColor = TEAL }) => (
  <div className={`${cardClass} mb-8`}>
    <div className="flex items-center mb-6">
      <Icon className="h-8 w-8 flex-shrink-0 mr-4" style={{ color: iconColor }} />
      <h2 className="text-2xl font-bold text-black" style={{ color: iconColor }}>
        {title}
      </h2>
    </div>
    {children}
  </div>
);

const Security = () => {
  return (
    <div className="min-h-screen bg-white text-black">
      <PublicHeader />
      <div className="pt-16 sm:pt-18 md:pt-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
        {/* Hero */}
        <section className="flex flex-col justify-center items-center w-full bg-white pt-16 pb-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="flex flex-col items-center w-full max-w-3xl gap-4"
          >
            <div
              className="p-4 rounded-2xl flex items-center justify-center"
              style={{ backgroundColor: "rgba(0, 191, 166, 0.1)", border: "1px solid rgba(0, 191, 166, 0.2)" }}
            >
              <Shield className="h-12 w-12" style={{ color: TEAL }} />
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-black text-center">
              Security & Compliance
            </h1>
            <p className="text-xl text-gray-600 text-center max-w-2xl">
              Your team's data security is our top priority. Learn about our
              comprehensive security measures, compliance certifications, and
              commitment to protecting your information.
            </p>
          </motion.div>
        </section>

        {/* Main Content */}
        <div className="max-w-4xl mx-auto pb-16">
          <div className="max-w-none">
            <SectionCard icon={Lock} title="1. Data Encryption">
              <h3 className="text-xl font-semibold mb-3 text-black">1.1 Encryption in Transit</h3>
              <ul className="text-gray-600 mb-6 space-y-2 list-disc list-inside">
                <li>TLS 1.3 encryption for all data transmission</li>
                <li>HTTPS enforcement across all endpoints</li>
                <li>Secure WebSocket connections for real-time features</li>
                <li>Certificate pinning for mobile applications</li>
              </ul>
              <h3 className="text-xl font-semibold mb-3 text-black">1.2 Encryption at Rest</h3>
              <ul className="text-gray-600 mb-6 space-y-2 list-disc list-inside">
                <li>AES-256 encryption for all stored data</li>
                <li>Encrypted database storage with key management</li>
                <li>Encrypted backups with separate encryption keys</li>
                <li>Hardware security modules (HSM) for key storage</li>
              </ul>
              <h3 className="text-xl font-semibold mb-3 text-black">1.3 Key Management</h3>
              <ul className="text-gray-600 space-y-2 list-disc list-inside">
                <li>Automated key rotation every 90 days</li>
                <li>Separate encryption keys per customer</li>
                <li>Secure key generation and distribution</li>
                <li>Key escrow and recovery procedures</li>
              </ul>
            </SectionCard>

            <SectionCard icon={Server} title="2. Infrastructure Security">
              <h3 className="text-xl font-semibold mb-3 text-black">2.1 Cloud Security</h3>
              <ul className="text-gray-600 mb-6 space-y-2 list-disc list-inside">
                <li>AWS/Azure/GCP with enterprise-grade security</li>
                <li>Multi-region deployment for redundancy</li>
                <li>Private network isolation and VPC configuration</li>
                <li>DDoS protection and traffic filtering</li>
              </ul>
              <h3 className="text-xl font-semibold mb-3 text-black">2.2 Network Security</h3>
              <ul className="text-gray-600 mb-6 space-y-2 list-disc list-inside">
                <li>Web Application Firewall (WAF) protection</li>
                <li>Intrusion Detection and Prevention Systems</li>
                <li>Network segmentation and access controls</li>
                <li>Real-time threat monitoring and response</li>
              </ul>
              <h3 className="text-xl font-semibold mb-3 text-black">2.3 Physical Security</h3>
              <ul className="text-gray-600 space-y-2 list-disc list-inside">
                <li>Tier 4 data centers with 24/7 security</li>
                <li>Biometric access controls and surveillance</li>
                <li>Environmental monitoring and fire suppression</li>
                <li>Redundant power and cooling systems</li>
              </ul>
            </SectionCard>

            <SectionCard icon={Users} title="3. Access Control & Authentication">
              <h3 className="text-xl font-semibold mb-3 text-black">3.1 Multi-Factor Authentication</h3>
              <ul className="text-gray-600 mb-6 space-y-2 list-disc list-inside">
                <li>TOTP-based MFA for all user accounts</li>
                <li>SMS and email verification options</li>
                <li>Hardware security key support (FIDO2)</li>
                <li>Adaptive authentication based on risk</li>
              </ul>
              <h3 className="text-xl font-semibold mb-3 text-black">3.2 Role-Based Access Control</h3>
              <ul className="text-gray-600 mb-6 space-y-2 list-disc list-inside">
                <li>Granular permissions and role definitions</li>
                <li>Principle of least privilege enforcement</li>
                <li>Regular access reviews and audits</li>
                <li>Just-in-time access provisioning</li>
              </ul>
              <h3 className="text-xl font-semibold mb-3 text-black">3.3 Session Management</h3>
              <ul className="text-gray-600 space-y-2 list-disc list-inside">
                <li>Secure session tokens with expiration</li>
                <li>Concurrent session limits</li>
                <li>Automatic logout on inactivity</li>
                <li>Session invalidation on security events</li>
              </ul>
            </SectionCard>

            <SectionCard icon={Eye} title="4. Monitoring & Detection">
              <h3 className="text-xl font-semibold mb-3 text-black">4.1 Security Monitoring</h3>
              <ul className="text-gray-600 mb-6 space-y-2 list-disc list-inside">
                <li>24/7 Security Operations Center (SOC)</li>
                <li>Real-time threat detection and analysis</li>
                <li>Behavioral analytics and anomaly detection</li>
                <li>Machine learning-based threat intelligence</li>
              </ul>
              <h3 className="text-xl font-semibold mb-3 text-black">4.2 Audit Logging</h3>
              <ul className="text-gray-600 mb-6 space-y-2 list-disc list-inside">
                <li>Comprehensive audit trails for all actions</li>
                <li>Immutable log storage and retention</li>
                <li>Real-time log analysis and alerting</li>
                <li>Compliance reporting and dashboards</li>
              </ul>
              <h3 className="text-xl font-semibold mb-3 text-black">4.3 Incident Response</h3>
              <ul className="text-gray-600 space-y-2 list-disc list-inside">
                <li>Defined incident response procedures</li>
                <li>Automated incident detection and escalation</li>
                <li>24/7 incident response team availability</li>
                <li>Post-incident analysis and improvement</li>
              </ul>
            </SectionCard>

            <SectionCard icon={Key} title="5. Compliance & Certifications">
              <h3 className="text-xl font-semibold mb-3 text-black">5.1 Security Certifications</h3>
              <ul className="text-gray-600 mb-6 space-y-2 list-disc list-inside">
                <li>SOC 2 Type II certification</li>
                <li>ISO 27001 Information Security Management</li>
                <li>ISO 27017 Cloud Security Controls</li>
                <li>ISO 27018 Privacy Controls for Cloud Services</li>
              </ul>
              <h3 className="text-xl font-semibold mb-3 text-black">5.2 Privacy Compliance</h3>
              <ul className="text-gray-600 mb-6 space-y-2 list-disc list-inside">
                <li>GDPR compliance for EU data subjects</li>
                <li>CCPA compliance for California residents</li>
                <li>HIPAA compliance for healthcare data</li>
                <li>Regular compliance audits and assessments</li>
              </ul>
              <h3 className="text-xl font-semibold mb-3 text-black">5.3 Industry Standards</h3>
              <ul className="text-gray-600 space-y-2 list-disc list-inside">
                <li>OWASP Top 10 security controls</li>
                <li>NIST Cybersecurity Framework alignment</li>
                <li>Cloud Security Alliance (CSA) STAR certification</li>
                <li>Regular penetration testing and assessments</li>
              </ul>
            </SectionCard>

            <SectionCard icon={AlertTriangle} title="6. Vulnerability Management">
              <h3 className="text-xl font-semibold mb-3 text-black">6.1 Security Testing</h3>
              <ul className="text-gray-600 mb-6 space-y-2 list-disc list-inside">
                <li>Regular penetration testing by third-party experts</li>
                <li>Automated vulnerability scanning and assessment</li>
                <li>Code security analysis and static testing</li>
                <li>Dynamic application security testing (DAST)</li>
              </ul>
              <h3 className="text-xl font-semibold mb-3 text-black">6.2 Patch Management</h3>
              <ul className="text-gray-600 mb-6 space-y-2 list-disc list-inside">
                <li>Automated security patch deployment</li>
                <li>Critical vulnerability response within 24 hours</li>
                <li>Regular dependency updates and maintenance</li>
                <li>Zero-day vulnerability monitoring and response</li>
              </ul>
              <h3 className="text-xl font-semibold mb-3 text-black">6.3 Security Development</h3>
              <ul className="text-gray-600 space-y-2 list-disc list-inside">
                <li>Secure software development lifecycle (SDLC)</li>
                <li>Security code reviews and peer validation</li>
                <li>Threat modeling and risk assessment</li>
                <li>Security training for all development teams</li>
              </ul>
            </SectionCard>

            <SectionCard icon={Shield} title="7. Data Protection">
              <h3 className="text-xl font-semibold mb-3 text-black">7.1 Data Classification</h3>
              <ul className="text-gray-600 mb-6 space-y-2 list-disc list-inside">
                <li>Automated data classification and labeling</li>
                <li>Sensitive data detection and protection</li>
                <li>Data loss prevention (DLP) controls</li>
                <li>Privacy-by-design implementation</li>
              </ul>
              <h3 className="text-xl font-semibold mb-3 text-black">7.2 Backup & Recovery</h3>
              <ul className="text-gray-600 mb-6 space-y-2 list-disc list-inside">
                <li>Automated daily encrypted backups</li>
                <li>Geographic redundancy and disaster recovery</li>
                <li>Point-in-time recovery capabilities</li>
                <li>Regular backup testing and validation</li>
              </ul>
              <h3 className="text-xl font-semibold mb-3 text-black">7.3 Data Retention</h3>
              <ul className="text-gray-600 space-y-2 list-disc list-inside">
                <li>Automated data lifecycle management</li>
                <li>Configurable retention policies per data type</li>
                <li>Secure data destruction procedures</li>
                <li>Compliance with legal retention requirements</li>
              </ul>
            </SectionCard>

            <SectionCard icon={Lock} title="8. Third-Party Security">
              <h3 className="text-xl font-semibold mb-3 text-black">8.1 Vendor Security</h3>
              <ul className="text-gray-600 mb-6 space-y-2 list-disc list-inside">
                <li>Comprehensive vendor security assessments</li>
                <li>Security questionnaires and audits</li>
                <li>Data processing agreements (DPAs)</li>
                <li>Regular vendor security reviews</li>
              </ul>
              <h3 className="text-xl font-semibold mb-3 text-black">8.2 API Security</h3>
              <ul className="text-gray-600 mb-6 space-y-2 list-disc list-inside">
                <li>OAuth 2.0 and OpenID Connect authentication</li>
                <li>API rate limiting and throttling</li>
                <li>Input validation and sanitization</li>
                <li>API security testing and monitoring</li>
              </ul>
              <h3 className="text-xl font-semibold mb-3 text-black">8.3 Integration Security</h3>
              <ul className="text-gray-600 space-y-2 list-disc list-inside">
                <li>Secure third-party integrations</li>
                <li>Encrypted data exchange protocols</li>
                <li>Integration security testing</li>
                <li>Monitoring of integration health and security</li>
              </ul>
            </SectionCard>

            <SectionCard icon={Users} title="9. Security Awareness">
              <h3 className="text-xl font-semibold mb-3 text-black">9.1 Employee Training</h3>
              <ul className="text-gray-600 mb-6 space-y-2 list-disc list-inside">
                <li>Annual security awareness training for all employees</li>
                <li>Phishing simulation and testing</li>
                <li>Security best practices and guidelines</li>
                <li>Incident response training and drills</li>
              </ul>
              <h3 className="text-xl font-semibold mb-3 text-black">9.2 Security Policies</h3>
              <ul className="text-gray-600 mb-6 space-y-2 list-disc list-inside">
                <li>Comprehensive security policy framework</li>
                <li>Regular policy reviews and updates</li>
                <li>Security policy enforcement and monitoring</li>
                <li>Policy violation detection and response</li>
              </ul>
              <h3 className="text-xl font-semibold mb-3 text-black">9.3 Security Culture</h3>
              <ul className="text-gray-600 space-y-2 list-disc list-inside">
                <li>Security-first mindset across the organization</li>
                <li>Security champions and advocates</li>
                <li>Regular security communications and updates</li>
                <li>Security innovation and continuous improvement</li>
              </ul>
            </SectionCard>

            <SectionCard icon={AlertTriangle} title="10. Security Reporting">
              <h3 className="text-xl font-semibold mb-3 text-black">10.1 Security Disclosures</h3>
              <ul className="text-gray-600 mb-6 space-y-2 list-disc list-inside">
                <li>Responsible disclosure program for security researchers</li>
                <li>Bug bounty program with competitive rewards</li>
                <li>Security advisory notifications</li>
                <li>Transparent security incident reporting</li>
              </ul>
              <h3 className="text-xl font-semibold mb-3 text-black">10.2 Security Metrics</h3>
              <ul className="text-gray-600 mb-6 space-y-2 list-disc list-inside">
                <li>Regular security performance reporting</li>
                <li>Security dashboard for customers</li>
                <li>Compliance status and audit reports</li>
                <li>Security improvement tracking and metrics</li>
              </ul>
              <h3 className="text-xl font-semibold mb-3 text-black">10.3 Contact Information</h3>
              <ul className="text-gray-600 space-y-2 list-disc list-inside">
                <li><strong>Security Team:</strong> <a href="mailto:hello@teamgrid.ai" className="text-[#00BFA6] hover:underline">hello@teamgrid.ai</a></li>
                <li><strong>Bug Reports:</strong> <a href="mailto:hello@teamgrid.ai" className="text-[#00BFA6] hover:underline">hello@teamgrid.ai</a></li>
                <li><strong>Security Inquiries:</strong> <a href="mailto:hello@teamgrid.ai" className="text-[#00BFA6] hover:underline">hello@teamgrid.ai</a></li>
                <li><strong>Response Time:</strong> Within 24 hours for critical issues</li>
              </ul>
            </SectionCard>

            <div className={`${cardClass} text-center`} style={{ borderColor: "rgba(0, 191, 166, 0.25)", backgroundColor: "rgba(0, 191, 166, 0.04)" }}>
              <h2 className="text-2xl font-bold mb-4 text-black">
                Your Security is Our Priority
              </h2>
              <p className="text-gray-600 leading-relaxed mb-6">
                At TeamGrid.ai, we understand that security is not just a
                feature—it's a fundamental requirement. Our comprehensive security
                program ensures that your team's data is protected with
                enterprise-grade security measures and industry-leading best
                practices.
              </p>
              <p className="text-gray-600 leading-relaxed">
                We're committed to maintaining the highest standards of security
                and continuously improving our security posture to protect your
                valuable data.
              </p>
            </div>
          </div>
        </div>
        </div>

        <Testimonials />
        <div className="py-12 md:py-16 px-4 sm:px-6 md:px-8 bg-white">
          <FAQ />
        </div>
        <ReadyToTransform />
        <Footer />
      </div>
    </div>
  );
};

export default Security;
